import React, { useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useGlobalQueries } from '@/hooks/use-global-queries';
import { FooterTopCollapse } from './footer-top-collapse';
import { FooterSocials } from './footer-socials';
import { Column } from '@/queries/footer';
import clsx from 'clsx';

export const FooterTop = () => {
    const navigate = useNavigate();
    const containerRef = useRef<HTMLDivElement>(null);
    const { pageQuery, footerQuery } = useGlobalQueries();

    const footerContent = useMemo(() => {
        const content = pageQuery?.data?.market_type === 'cluster' ? footerQuery?.data?.clusterColumns : footerQuery?.data?.columns;
        return content || [];
    }, [pageQuery.data, footerQuery.data]);

    useEffect(() => {
        if (!containerRef.current) {
            return;
        }

        containerRef.current.querySelectorAll('a').forEach(link => {
            const url = new URL(link.href);
            if (link.classList.contains('social-link')) {
                return;
            }
            if (window?.location?.hostname !== url.hostname) {
                link.classList.add('external-link');
            }
        });
    }, [containerRef]);

    const handleClick = (e: React.MouseEvent) => {
        const target = e.target as HTMLElement;
        const link = target.closest('a') as HTMLAnchorElement | null;

        if (link && link.getAttribute('href')) {
            const href = link.getAttribute('href')!;

            // internal links only
            if (!/^https?:\/\//i.test(href)) {
                e.preventDefault();
                navigate(href);
            }
        }
    };

    const renderFooterTopDesktop = (column: Column) => (
        <div>
            <p className="r-mb-2 r-text-lg r-font-bold">{column.header}</p>
            <div
                tabIndex={-1} // -1 prevents focus on the element. As this is the parent element with an onClick handler, we don't want it to be focusable.
                onClick={handleClick}
                dangerouslySetInnerHTML={{ __html: column.content }}
                className="r-text-sm r-font-medium r-leading-6"
            />
            {column.options?.socials && footerQuery.data?.socialLinks && <FooterSocials socialLinks={footerQuery.data?.socialLinks} />}
            {column.options?.qrcolumn && footerQuery.data?.qr?.qrImg && (
                <div className="r-mt-12">
                    <h2 className="r-mb-2 r-text-lg r-font-bold">{footerQuery.data.qr.qr_header || ''}</h2>
                    <div className="r-size-20" dangerouslySetInnerHTML={{ __html: footerQuery.data.qr.qrImg }} />
                </div>
            )}
        </div>
    );

    const renderFooterTopMobile = (column: Column, index: number) => (
        <FooterTopCollapse title={column.header} index={index + 1} content={column.content} />
    );

    const { gridClass, colspanClass } = useMemo(() => {
        switch (footerContent.length) {
            case 6:
                return {
                    gridClass: 'r-grid-cols-3 lg:r-grid-cols-6',
                    colspanClass: 'r-col-span-3 lg:r-col-span-6',
                };
            case 5:
                return {
                    gridClass: 'r-grid-cols-3 lg:r-grid-cols-5',
                    colspanClass: 'r-col-span-3 lg:r-col-span-5',
                };
            case 4:
                return {
                    gridClass: 'r-grid-cols-2 lg:r-grid-cols-4',
                    colspanClass: 'r-col-span-2 lg:r-col-span-4',
                };
            case 3:
                return {
                    gridClass: 'r-grid-cols-1 lg:r-grid-cols-3',
                    colspanClass: 'lg:r-col-span-3',
                };
            case 2:
                return {
                    gridClass: 'r-grid-cols-1 lg:r-grid-cols-2',
                    colspanClass: 'lg:r-col-span-2',
                };
            case 1:
            default:
                return {
                    gridClass: 'r-grid-cols-1 lg:r-grid-cols-1',
                    colspanClass: 'lg:r-col-span-1',
                };
        }
    }, [footerContent.length]);

    return (
        <div data-component="footer-top" className="footer-top r-bg-tertiary-500 r-text-white">
            <div
                ref={containerRef}
                className={clsx(
                    'r-container r-z-50 r-flex r-flex-col r-flex-wrap r-justify-between r-gap-4 r-px-4 r-pb-16 r-pt-4 sm:r--mt-4 sm:r-grid sm:r-justify-start sm:r-pt-0 md:r-px-8 lg:r-gap-y-0',
                    gridClass
                )}
            >
                <div className={clsx('r-relative r-w-full r-pb-10', colspanClass)}>
                    <img className="r-w-32 md:r-w-40" src="/assets/logos/resurs_logo_rgb_white.svg" alt="Resursbank" />
                </div>
                {footerContent.map((column, index: number) => (
                    <div className="r-col-span-1" key={index}>
                        <div className="r-z-20 r-col-span-1 lg:r-hidden">{renderFooterTopMobile(column, index)}</div>
                        <div className="r-hidden lg:r-block">{renderFooterTopDesktop(column)}</div>
                    </div>
                ))}
                {footerQuery.data?.qr?.qrImg && (
                    <div className="r-col-start-1 r-pt-1 max-sm:r-hidden lg:r-hidden">
                        <p className="r-mb-2 r-text-lg r-font-bold">{footerQuery.data.qr.qr_header || ''}</p>
                        <div className="r-size-20" dangerouslySetInnerHTML={{ __html: footerQuery.data.qr.qrImg }} />
                    </div>
                )}
                <div className={clsx('r-flex r-items-end lg:r-hidden')}>
                    {footerQuery.data?.socialLinks && <FooterSocials socialLinks={footerQuery.data?.socialLinks} />}
                </div>
            </div>
        </div>
    );
};
